import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Activity, ArrowLeft, ShieldCheck } from 'lucide-react'
import {
  fetchAdminUser,
  fetchAdminUserActivityLogs,
  fetchUserRoleAssignments,
} from '../api'
import { SecuritySessionsTable } from '@/features/settings/components/SecuritySessionsTable'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { useAppBreadcrumbTrail } from '@/layouts/AppShell'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatAppDateTime } from '@/lib/locale'
import { cn } from '@/lib/utils'

const ROLE_BADGE_COLORS: Record<string, string> = {
  'super-admin':
    'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300',
  'business-owner':
    'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
  agent:
    'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
}

function userInitials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

function groupPermissions(perms: string[]): Record<string, string[]> {
  return perms.reduce(
    (acc, p) => {
      const dot = p.indexOf('.')
      const resource = dot >= 0 ? p.slice(0, dot) : p
      const action = dot >= 0 ? p.slice(dot + 1) : p
      if (!acc[resource]) acc[resource] = []
      acc[resource].push(action)
      return acc
    },
    {} as Record<string, string[]>,
  )
}

export function AdminUserDetailPage() {
  const { t } = useTranslation()
  const { userId } = useParams<{ userId: string }>()
  const userQuery = useQuery({
    queryKey: ['admin', 'users', 'detail', userId],
    queryFn: () => fetchAdminUser(userId!),
    enabled: Boolean(userId),
  })

  const logsQuery = useQuery({
    queryKey: ['admin', 'user-activity', userId],
    queryFn: () => fetchAdminUserActivityLogs(userId!),
    enabled: Boolean(userId),
  })

  const rolesQuery = useQuery({
    queryKey: ['admin', 'user-roles', userId],
    queryFn: () => fetchUserRoleAssignments(userId!),
    enabled: Boolean(userId),
  })

  const user = userQuery.data?.data

  useAppBreadcrumbTrail(
    user
      ? [
          { label: t('admin.tabs.allUsers'), to: '/admin?tab=all-users' },
          { label: user.display_name },
        ]
      : null,
  )

  if (userQuery.isPending) {
    return (
      <section className="app-section">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="mt-2 flex items-center gap-3">
          <Skeleton className="h-7 w-48" />
        </div>
        <div className="mt-4 rounded-xl bg-muted/30 p-5">
          <div className="flex gap-4">
            <Skeleton className="size-14 rounded-full shrink-0" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-56" />
              <div className="flex gap-1">
                <Skeleton className="h-5 w-20 rounded-full" />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <Skeleton className="h-8 w-64 rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </section>
    )
  }

  if (!user) {
    return (
      <section className="app-section">
        <p className="text-sm text-muted-foreground">User not found.</p>
      </section>
    )
  }

  const logs = logsQuery.data?.data ?? []
  const roleAssignments = rolesQuery.data?.data ?? []
  const effectivePermissions = user.effective_permissions ?? []
  const permGroups = groupPermissions(effectivePermissions)

  return (
    <section className="app-section">
      <Tabs defaultValue="activity">
        <PageHeader
          title={user.display_name}
          beforeTitle={
            <Link
              to="/admin?tab=all-users"
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="size-4" />
              {t('admin.userDetail.backToUsers')}
            </Link>
          }
          right={
            <PageHeaderToolbar>
              <TabsList>
                <TabsTrigger value="activity">
                  <span className="inline-flex items-center gap-1.5">
                    <Activity className="size-3.5" />
                    <span>{t('admin.userDetail.tabs.activity')}</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="access">
                  <span className="inline-flex items-center gap-1.5">
                    <ShieldCheck className="size-3.5" />
                    <span>{t('admin.userDetail.tabs.access')}</span>
                  </span>
                </TabsTrigger>
              </TabsList>
            </PageHeaderToolbar>
          }
        />

        {/* Profile card */}
        <div className="mt-4 rounded-xl border border-border bg-card p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <Avatar className="size-14">
              <AvatarImage src={user.avatar_url ?? undefined} alt={user.display_name} />
              <AvatarFallback className="text-lg font-semibold">
                {userInitials(user.display_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-1 flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{user.display_name}</span>
                {user.is_foundation && (
                  <Badge variant="secondary" className="text-xs">
                    {t('admin.users.foundationBadge')}
                  </Badge>
                )}
                <Badge
                  variant="outline"
                  className={cn(
                    'capitalize',
                    user.status === 'active'
                      ? 'border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-400'
                      : user.status === 'suspended'
                        ? 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400'
                        : 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
                  )}
                >
                  {t(`admin.users.status.${user.status}` as const, { defaultValue: user.status })}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{user.email}</p>
              <div className="flex flex-wrap gap-1">
                {user.roles.map((r) => (
                  <Badge
                    key={r.id}
                    variant="outline"
                    className={cn('text-xs', ROLE_BADGE_COLORS[r.slug] ?? '')}
                  >
                    {r.name}
                  </Badge>
                ))}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                <span>{t('admin.allUsers.table.createdAt')}: {formatAppDateTime(user.created_at)}</span>
                {user.last_login_at && (
                  <span>{t('admin.allUsers.table.lastLogin')}: {formatAppDateTime(user.last_login_at)}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity */}
        <TabsContent value="activity" className="mt-4">
          {logsQuery.isPending || logs.length > 0 ? (
            <SecuritySessionsTable logs={logs} isLoading={logsQuery.isPending} />
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('admin.userDetail.noActivity')}
            </p>
          )}
        </TabsContent>

        {/* Roles & Permissions (read-only) */}
        <TabsContent value="access" className="mt-4 space-y-8">
          {/* Roles section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t('admin.userDetail.access.rolesTitle')}
            </h3>
            {rolesQuery.isPending ? (
              <Skeleton className="h-24 w-full rounded-lg" />
            ) : roleAssignments.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('admin.assignments.noRoles')}
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('admin.assignments.table.role')}</TableHead>
                        <TableHead>{t('admin.assignments.table.scope')}</TableHead>
                        <TableHead>{t('admin.assignments.table.assignedAt')}</TableHead>
                        <TableHead>{t('admin.assignments.table.expiresAt')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {roleAssignments.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>
                            <Badge variant="secondary">{a.role.name}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {t(`admin.assignments.scopes.${a.scope_type}` as const, { defaultValue: a.scope_type })}
                            {a.business_id && (
                              <span className="ml-1 text-xs opacity-60">({a.business_id.slice(0, 8)}…)</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatAppDateTime(a.assigned_at)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {a.expires_at ? formatAppDateTime(a.expires_at) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          {/* Effective permissions section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t('admin.userDetail.access.permissionsTitle')}
            </h3>
            {userQuery.isPending ? (
              <Skeleton className="h-32 w-full rounded-lg" />
            ) : effectivePermissions.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {t('admin.userDetail.access.noPermissions')}
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(permGroups)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([resource, actions]) => (
                    <div key={resource}>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {resource}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {actions.sort().map((action) => (
                          <Badge
                            key={action}
                            variant="outline"
                            className="rounded-full px-2.5 py-0.5 text-xs font-normal"
                          >
                            {action}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </section>
  )
}
